import {
   defaultUserLocaleByPhpLangid,
   fromPhpLangId,
   type PhpLangid,
   toPhpLangId,
} from '@ifixit/i18n/php-langid';
import type { LocaleCode } from '@ifixit/i18n/types';
import {
   type UserLocaleCode,
   defaultUserLocaleByRouteLocale,
   parseUserLocale,
   routeLocaleByUserLocale,
   userLocalesByRouteLocale,
} from '@ifixit/i18n/user-locale';

export interface UserLocaleSignals {
   /** Raw `user-locale` cookie value (explicit choice). */
   cookieValue: string | null | undefined;
   /** PHP `view-locale` cookie / stored route locale preference. */
   phpViewLocale: string | null | undefined;
   /** PHP `view-langid` cookie / stored language preference. */
   phpViewLangid: string | null | undefined;
   /**
    * Geo-IP-derived user locale.
    */
   geoUserLocale: UserLocaleCode | null;
}

function resolveUserLocale(
   signals: UserLocaleSignals,
   match: (userLocale: UserLocaleCode | null) => UserLocaleCode | null,
   defaultUserLocale: UserLocaleCode | undefined
): UserLocaleCode {
   return (
      match(parseRawCookieValue(signals.cookieValue)) ??
      match(resolvePhpPreference(signals.phpViewLocale, signals.phpViewLangid)) ??
      match(signals.geoUserLocale) ??
      defaultUserLocale ??
      'en-US'
   );
}

/**
 * Resolve the user locale for a Next.js route locale (e.g. `en-us`).
 *
 * Precedence: explicit cookie → stored PHP preference → geo guess →
 * route-locale default → `en-US`.
 */
export function resolveUserLocaleForRouteLocale(
   currentRouteLocale: LocaleCode,
   signals: UserLocaleSignals
): UserLocaleCode {
   return resolveUserLocale(
      signals,
      userLocale => matchingRouteLocale(userLocale, currentRouteLocale),
      defaultUserLocaleByRouteLocale[currentRouteLocale]
   );
}

/**
 * Resolve the user locale for a Carpenter PHP language id (e.g. `en`, `fr`).
 *
 * Precedence: explicit cookie → stored PHP preference → geo guess →
 * language-home default → `en-US`.
 */
export function resolveUserLocaleForPhpLangId(
   currentPhpLangId: PhpLangid,
   signals: UserLocaleSignals
): UserLocaleCode {
   return resolveUserLocale(
      signals,
      userLocale => matchingPhpLangId(userLocale, currentPhpLangId),
      defaultUserLocaleByPhpLangid[currentPhpLangId]
   );
}

export function parseRawCookieValue(value: string | null | undefined): UserLocaleCode | null {
   if (value == null) return null;
   return value in routeLocaleByUserLocale ? (value as UserLocaleCode) : null;
}

function matchingRouteLocale(
   userLocale: UserLocaleCode | null,
   currentRouteLocale: LocaleCode
): UserLocaleCode | null {
   if (!userLocale) return null;
   return routeLocaleByUserLocale[userLocale] === currentRouteLocale ? userLocale : null;
}

function matchingPhpLangId(
   userLocale: UserLocaleCode | null,
   currentPhpLangid: string
): UserLocaleCode | null {
   if (!userLocale) return null;
   return toPhpLangId(userLocale.slice(0, 2)) === currentPhpLangid ? userLocale : null;
}

/**
 * Find a user locale matching the PHP view locale and view langid. Falls back
 * to the store's default.
 */
function resolvePhpPreference(
   viewLocale: string | null | undefined,
   viewLangid: string | null | undefined
): UserLocaleCode | null {
   if (viewLocale == null) return null;

   const candidates = userLocalesByRouteLocale[viewLocale as LocaleCode];
   if (!candidates) return null;

   if (viewLangid) {
      const langid = fromPhpLangId(viewLangid);
      const langMatch = candidates.find(locale => parseUserLocale(locale).language === langid);
      if (langMatch) return langMatch;
   }

   return defaultUserLocaleByRouteLocale[viewLocale as LocaleCode] ?? null;
}
